import { Team, SlimTeam } from '../types/Team';
import { Station } from '../types/Station';

const PROD_API_URL = 'https://api.pannbenadmin.se';

const API_URL = process.env.REACT_APP_API_URL || PROD_API_URL;

export const checkAuth = async (): Promise<boolean> => {
  const response = await fetch(`${API_URL}/auth/check`, {
    credentials: 'include'
  });
  return response.ok;
};

export const fetchTeamsData = async (category: string): Promise<Team[]> => {
  const response = await fetch(`${API_URL}/results?category=${category}`, {
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch teams');
  }
  const data = await response.json();
  return data.results;
};

export const fetchTeamById = async (teamId: string): Promise<Team> => {
  const response = await fetch(`${API_URL}/teams/${teamId}`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch team');
  }
  return response.json();
};

export const fetchTeams = async (query: string): Promise<SlimTeam[]> => {
  const response = await fetch(`${API_URL}/teams?query=${query}`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch team');
  }
  const data = await response.json();
  return data.teams;
};

export const createTeam = async (name: string, category: string, participants: string[]): Promise<boolean> => {
  const response = await fetch(`${API_URL}/teams`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ name, category, participants }),
  });

  if (!response.ok) {
    throw new Error('Failed to create team');
  }

  return true;
};

export const updateTeam = async (teamId: string, name: string, category: string, participants: string[]): Promise<boolean> => {
  const response = await fetch(`${API_URL}/teams/${teamId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ name, category, participants }),
  });

  if (!response.ok) {
    throw new Error('Failed to update team');
  }

  return true;
};

export const deleteTeam = async (teamId: string): Promise<boolean> => {
  const response = await fetch(`${API_URL}/teams/${teamId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
  });

  if (!response.ok) {
    throw new Error('Failed to delete team');
  }

  return true;
};

export const fetchStations = async (): Promise<Station[]> => {
  const response = await fetch(`${API_URL}/stations`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch stations');
  }
  const data = await response.json();
  return data.stations;
};

export const createStation = async (station: Station): Promise<boolean> => {
  const response = await fetch(`${API_URL}/stations`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(station),
  });
  if (!response.ok) {
    throw new Error('Failed to create stations');
  }
  return true
};

export const deleteStation = async (stationId: string): Promise<boolean> => {
  const response = await fetch(`${API_URL}/stations/${stationId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
  });
  if (!response.ok) {
    throw new Error('Failed to delete stations');
  }
  return true
};

export const login = async (username: string, password: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    });

    if (response.status === 401) {
      throw new Error('Username or password wrong');
    }
    
    if (!response.ok) {
      throw new Error('Login failed - please try again later');
    }

    return true;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

export const logout = async (): Promise<void> => {
  await fetch(`${API_URL}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
};

export interface StationPause {
  teamId: string;
  stationId: string;
}

export interface HeatDto {
  id: string;
  name: string;
}

export interface HeatsDto {
  heats: HeatDto[];
}

export interface TeamDto {
  id: string;
  name: string;
  category: string;
  members: string[];
}

export interface TeamsInHeatDto {
  teams: TeamDto[];
}

export const fetchHeats = async (): Promise<HeatDto[]> => {
  const response = await fetch(`${API_URL}/heats`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch heats');
  }
  const data = await response.json();
  return data.heats;
};

export const createHeat = async (heat: HeatDto): Promise<boolean> => {
  const response = await fetch(`${API_URL}/heats`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(heat),
  });
  if (!response.ok) {
    throw new Error('Failed to create heat');
  }
  return true;
};

export const deleteHeat = async (heatId: string): Promise<boolean> => {
  const response = await fetch(`${API_URL}/heats/${heatId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
  });
  if (!response.ok) {
    throw new Error('Failed to delete heat');
  }
  return true;
};

export const fetchTeamsInHeat = async (heatId: string): Promise<TeamDto[]> => {
  const response = await fetch(`${API_URL}/heats/${heatId}/teams`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch teams in heat');
  }
  const data = await response.json();
  return data.teams;
};

export const addTeamToHeat = async (heatId: string, teamId: string): Promise<boolean> => {
  const response = await fetch(`${API_URL}/heats/${heatId}/teams/${teamId}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
  });

  if (response.status === 409) {
    throw new Error('Team is already assigned to another heat');
  }

  if (!response.ok) {
    throw new Error('Failed to add team to heat');
  }

  return true;
};

export const removeTeamFromHeat = async (heatId: string, teamId: string): Promise<boolean> => {
  const response = await fetch(`${API_URL}/heats/${heatId}/teams/${teamId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
  });
  if (!response.ok) {
    throw new Error('Failed to remove team from heat');
  }
  return true;
};

export const fetchTeamsNotInHeat = async (query: string): Promise<TeamDto[]> => {
  const response = await fetch(`${API_URL}/heats/not-in-heat?query=${query}`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch teams not in heat');
  }
  const data = await response.json();
  return data;
};

export const fetchNotStartedHeats = async (): Promise<HeatDto[]> => {
  const response = await fetch(`${API_URL}/race-management/not-started-heats`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch not started heats');
  }
  const data = await response.json();
  return data;
};

export const fetchStartedHeats = async (): Promise<HeatDto[]> => {
  const response = await fetch(`${API_URL}/race-management/started-heats`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch started heats');
  }
  const data = await response.json();
  return data;
};

export const startHeat = async (heatId: string): Promise<boolean> => {
  const response = await fetch(`${API_URL}/race-management/start/${heatId}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
  });

  if (!response.ok) {
    throw new Error('Failed to start heat');
  }

  return true;
};

export const stopTeam = async (teamId: string): Promise<boolean> => {
  const response = await fetch(`${API_URL}/race-management/stop/${teamId}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
  });

  if (!response.ok) {
    throw new Error('Failed to stop team');
  }

  return true;
};

export const createOrUpdatePause = async (teamId: string, stationId: string, startEventTime: string, endEventTime: string): Promise<boolean> => {
  const response = await fetch(`${API_URL}/events/manual-pause`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ teamId, stationId, startEventTime, endEventTime }),
  });

  if (!response.ok) {
    throw new Error('Failed to create or update manual pause');
  }

  return true;
};

export const deletePause = async (stationId: string, teamId: string): Promise<boolean> => {
  const response = await fetch(`${API_URL}/events/station/${stationId}/team/${teamId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to create or update manual pause');
  }

  return true;
};

export const fetchTeamsForFinish = async (query: string): Promise<SlimTeam[]> => {
  const response = await fetch(`${API_URL}/race-management/teams-for-finish?query=${query}`, {
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error('Failed to fetch team');
  }
  const data = await response.json();
  return data.teams;
};